<script setup>
import { computed } from 'vue'
import { Thermometer, Droplets, Wind, Sun, Cloud, CloudRain, SprayCan, Sprout } from 'lucide-vue-next'

const props = defineProps({
  stats: { type: Object, default: null },
})

const FORECAST_ICON = { sun: Sun, cloud: Cloud, rain: CloudRain }

const rain7d = computed(() =>
  props.stats ? Math.round(props.stats.agro.forecast.reduce((a, b) => a + b.rainMm, 0)) : 0
)
</script>

<template>
  <section v-if="!stats">
    <h2 class="text-sm font-semibold mb-2">Weather</h2>
    <div class="rounded-lg border bg-card p-3">
      <div class="h-5 w-24 rounded bg-muted animate-pulse"></div>
      <div class="mt-3 h-16 rounded bg-muted animate-pulse"></div>
    </div>
  </section>
  <section v-else>
    <h2 class="text-sm font-semibold mb-2">Weather</h2>
    <div class="rounded-lg border bg-card p-3">
      <div class="mb-2 text-sm font-medium text-muted-foreground">Now</div>
      <div class="grid grid-cols-3 gap-2">
        <div class="flex items-center gap-2">
          <Thermometer class="size-4 shrink-0 text-[#eb6834]" />
          <div>
            <div class="text-sm font-bold leading-none">{{ stats.weather.tempC.toFixed(0) }}°C</div>
            <div class="text-xs text-muted-foreground mt-0.5">Temp</div>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <Droplets class="size-4 shrink-0 text-[#2a78d6]" />
          <div>
            <div class="text-sm font-bold leading-none">{{ stats.weather.humidityPct.toFixed(0) }}%</div>
            <div class="text-xs text-muted-foreground mt-0.5">Humidity</div>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <Wind class="size-4 shrink-0 text-muted-foreground" />
          <div>
            <div class="text-sm font-bold leading-none">{{ stats.weather.windMs.toFixed(1) }} m/s</div>
            <div class="text-xs text-muted-foreground mt-0.5">Wind</div>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-2 rounded-lg border bg-card p-3">
      <div class="mb-2 flex items-baseline justify-between">
        <div class="text-sm font-medium text-muted-foreground">Next 7 days</div>
        <div v-if="rain7d" class="text-xs font-medium text-[#2a78d6]">≈ {{ rain7d }} mm rain expected</div>
        <div v-else class="text-xs text-muted-foreground">no rain expected</div>
      </div>
      <div class="grid grid-cols-7 gap-1 text-center">
        <div
          v-for="(d, di) in stats.agro.forecast"
          :key="d.date"
          class="min-w-0 rounded-md py-1"
          :class="di === 0 ? 'bg-muted' : ''"
        >
          <div class="text-xs" :class="di === 0 ? 'font-semibold' : 'text-muted-foreground'">
            {{ di === 0 ? 'Today' : new Date(d.date).toLocaleDateString('en-US', { weekday: 'short' }) }}
          </div>
          <component
            :is="FORECAST_ICON[d.icon]"
            class="size-4 mx-auto my-1"
            :class="d.icon === 'rain' ? 'text-[#2a78d6]' : d.icon === 'sun' ? 'text-[#eda100]' : 'text-muted-foreground'"
          />
          <div class="text-xs font-semibold">{{ d.tempC.toFixed(0) }}°</div>
          <div class="text-xs" :class="d.rainMm ? 'font-medium text-[#2a78d6]' : 'text-muted-foreground'">
            {{ d.rainMm ? d.rainMm + 'mm' : '—' }}
          </div>
        </div>
      </div>
      <div class="mt-2 flex items-center gap-2 rounded-md bg-primary/5 px-2.5 py-1.5 text-sm">
        <SprayCan class="size-4 shrink-0 text-primary" />
        <span class="text-muted-foreground">Best spray window:</span>{{ ' ' }}
        <span class="font-semibold">{{ stats.agro.sprayWindow }}</span>
      </div>
    </div>

    <div class="mt-2 rounded-lg border bg-card p-3">
      <div class="mb-2 flex items-baseline justify-between">
        <div class="text-sm font-medium text-muted-foreground">This season</div>
        <div class="text-xs text-muted-foreground">
          {{ new Date(stats.agro.seasonStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) }} – {{ new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) }}
        </div>
      </div>
      <div class="space-y-1.5 text-sm">
        <div class="flex items-center gap-2">
          <CloudRain class="size-4 shrink-0 text-[#2a78d6]" />
          <span class="font-semibold">{{ stats.agro.seasonRainMm }} mm</span>{{ ' ' }}
          <span class="text-muted-foreground truncate">
            rain · {{ stats.agro.seasonRainDeltaPct >= 0 ? '+' : '' }}{{ stats.agro.seasonRainDeltaPct }}% vs 10-yr average
          </span>
        </div>
        <div class="flex items-center gap-2">
          <Sprout class="size-4 shrink-0 text-[#008300]" />
          <span class="font-semibold">{{ stats.agro.gdd.toLocaleString('en-US') }} GDD</span>{{ ' ' }}
          <span class="text-muted-foreground truncate">accumulated (base 10°C)</span>
        </div>
      </div>
    </div>
  </section>
</template>
